import React, { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation } from '@tanstack/react-query';
import { shippingService, OrderItemForReceive, OrderForReceive } from '../../api/services/shippingService';
import { LabelPrintedOrder, ShippingOrder, ShippingStationOrder } from '@shared/types';
import { toast } from 'react-hot-toast';

export default function ShippingStation() {
  const [order, setOrder] = useState<ShippingOrder | null>(null);
  const [showPrintView, setShowPrintView] = useState<boolean>(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Receive modal state
  const [showReceiveModal, setShowReceiveModal] = useState<boolean>(false);
  const [receiveOrderNumber, setReceiveOrderNumber] = useState<string>('');
  const [receiveOrder, setReceiveOrder] = useState<OrderForReceive | null>(null);
  const [receiveItems, setReceiveItems] = useState<OrderItemForReceive[]>([]);
  const [scanInput, setScanInput] = useState<string>('');
  const [allReceived, setAllReceived] = useState<boolean>(false);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Reprint modal state
  const [showReprintModal, setShowReprintModal] = useState<boolean>(false);
  const [reprintOrderNumber, setReprintOrderNumber] = useState<string>('');
  const reprintInputRef = useRef<HTMLInputElement>(null);

  // Fetch shipping station list
  const { data: listData, isLoading, refetch } = useQuery({
    queryKey: ['shipping-station-list', currentPage, searchQuery],
    queryFn: () => shippingService.getShippingStationList(currentPage, searchQuery || undefined),
  });

  // Print effect
  const hasPrintedRef = useRef(false);
  useEffect(() => {
    if (showPrintView && order) {
      if (hasPrintedRef.current) return;
      hasPrintedRef.current = true;
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
    hasPrintedRef.current = false;
  }, [showPrintView, order]);

  // Focus scan input when modal opens
  useEffect(() => {
    if (showReceiveModal && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [showReceiveModal]);

  // Focus reprint input when modal opens
  useEffect(() => {
    if (showReprintModal && reprintInputRef.current) {
      reprintInputRef.current.focus();
    }
  }, [showReprintModal]);

  // Get order items mutation
  const getOrderItemsMutation = useMutation({
    mutationFn: (orderNum: string) => shippingService.getOrderItemsForReceive(orderNum),
    onSuccess: (response) => {
      if (response.success) {
        setReceiveOrder(response.order);
        setReceiveItems(response.items);
        setAllReceived(response.allReceived);
        setShowReceiveModal(true);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to fetch order items');
    },
  });

  // Receive item mutation
  const receiveItemMutation = useMutation({
    mutationFn: (itemNumber: string) => shippingService.receiveItem(itemNumber),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(`Received: ${response.item?.partNumber}`);
        // Update the local state
        setReceiveItems(prev => prev.map(item =>
          item.itemNumber === response.item?.itemNumber
            ? { ...item, isReceived: true, status: 'Received', actionId: 9 }
            : item
        ));
        // Check if all items are now received
        const updatedItems = receiveItems.map(item =>
          item.itemNumber === response.item?.itemNumber
            ? { ...item, isReceived: true }
            : item
        );
        const nowAllReceived = updatedItems.every(item => item.isReceived);
        setAllReceived(nowAllReceived);

        if (nowAllReceived) {
          toast.success('All items received! Ready to print.');
        }
      }
      setScanInput('');
      scanInputRef.current?.focus();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to receive item');
      setScanInput('');
      scanInputRef.current?.focus();
    },
  });

  // Get order for printing mutation
  const getOrderMutation = useMutation({
    mutationFn: (orderNum: string) => shippingService.getOrderForShipping(orderNum),
    onSuccess: (response) => {
      if (response.success && response.order) {
        setOrder(response.order);
        setShowReceiveModal(false);
        setShowPrintView(true);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to fetch order for printing');
    },
  });

  // Reprint order mutation
  const reprintOrderMutation = useMutation({
    mutationFn: (orderNum: string) => shippingService.getOrderForShipping(orderNum),
    onSuccess: (response) => {
      if (response.success && response.order) {
        setOrder(response.order);
        setShowReprintModal(false);
        setReprintOrderNumber('');
        setShowPrintView(true);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Order not found or failed to fetch');
    },
  });

  const handleReceive = (orderNumber: string) => {
    setReceiveOrderNumber(orderNumber);
    setScanInput('');
    getOrderItemsMutation.mutate(orderNumber);
  };

  const handleScanSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (scanInput.trim()) {
      receiveItemMutation.mutate(scanInput.trim());
    }
  };

  const handlePrint = () => {
    if (receiveOrderNumber) {
      getOrderMutation.mutate(receiveOrderNumber);
    }
  };

  const handleCloseModal = () => {
    setShowReceiveModal(false);
    setReceiveOrderNumber('');
    setReceiveOrder(null);
    setReceiveItems([]);
    setAllReceived(false);
    setScanInput('');
    refetch();
  };

  const handleClosePrintView = () => {
    setShowPrintView(false);
    setOrder(null);
    refetch();
  };

  const handleOpenReprintModal = () => {
    setShowReprintModal(true);
    setReprintOrderNumber('');
  };

  const handleCloseReprintModal = () => {
    setShowReprintModal(false);
    setReprintOrderNumber('');
  };

  const handleReprintSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reprintOrderNumber.trim()) {
      reprintOrderMutation.mutate(reprintOrderNumber.trim());
    }
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      toast.success('Shipping list refreshed');
    } catch (error) {
      toast.error('Failed to refresh shipping list');
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatStatusDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('shipped') || statusLower.includes('transit')) {
      return 'bg-gray-600 text-white';
    } else if (statusLower.includes('received') || statusLower.includes('ready')) {
      return 'bg-green-600 text-white';
    } else if (statusLower.includes('label')) {
      return 'bg-green-700 text-white';
    } else if (statusLower.includes('sewing')) {
      return 'bg-blue-600 text-white';
    }
    return 'bg-gray-500 text-white';
  };

  const calculateSubtotal = (): number => {
    if (!order) return 0;
    return order.Items.reduce((sum, item) => {
      return item.PartNumber === 'DISCOUNT' ? sum : sum + (item.Price || 0);
    }, 0);
  };

  const calculateDiscount = (): number => {
    if (!order) return 0;
    const discountItem = order.Items.find(item => item.PartNumber === 'DISCOUNT');
    return discountItem ? Math.abs(discountItem.Price || 0) : 0;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const filteredOrders = listData?.orders ?? [];

  const labelPrintedOrders: LabelPrintedOrder[] = listData?.labelPrintedOrders ?? [];
  const totalOrders = listData?.totalOrders ?? 0;
  const pageSize = listData?.pageSize ?? 25;
  const totalPages = Math.ceil(totalOrders / pageSize);

  const calculateCODAmount = (): number => {
    if (!order) return 0;
    const itemTotal = order.Items.reduce((sum, item) => sum + item.Price, 0);
    return order.Total - order.Freight - itemTotal;
  };

  // Print view - generates correct number of pages based on DropShip
  // Non-dropship: 2 invoices
  // Dropship: 1 invoice + 1 packing list
  if (showPrintView && order) {
    // Build print array: [false, DropShip] means first page is invoice, second is packing list only if DropShip
    // For non-dropship: we want 2 invoices, so [false, false]
    // For dropship: we want 1 invoice + 1 packing list, so [false, true]
    const printArray = order.DropShip ? [false, true] : [false, false];

    return (
      <div className="print-view">
        <style>{`
          @media print {
            html, body { margin: 0; padding: 0; font-size: 11px; }
            .print-view { margin: 0; padding: 0; }
            .invoice-page {
              width: 100%;
              min-height: auto;
              padding: 0.3in 0.4in;
              page-break-inside: avoid;
              box-sizing: border-box;
            }
            .invoice-page:not(:last-child) { page-break-after: always; }
            .print-header { font-size: 10px; }
            .print-title { font-size: 18px; }
            .print-number { font-size: 14px; }
            .print-table { font-size: 10px; }
            .print-table td, .print-table th { padding: 3px 5px; }
            .print-footer { font-size: 10px; margin-top: 10px; }
            .print-total { font-size: 12px; }
            .remit-banner { font-size: 11px; padding: 8px; margin-top: 15px; }
            .nowrap { white-space: nowrap; }
          }
          @media screen {
            .print-view { padding: 20px; background: white; }
            .invoice-page { border: 1px solid #ccc; margin-bottom: 20px; padding: 20px; }
          }
          @media print {
            .print-view-toolbar { display: none; }
          }
        `}</style>

        <div className="print-view-toolbar flex justify-end gap-2 mb-4">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Print
          </button>
          <button
            onClick={handleClosePrintView}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>

        {printArray.map((isPacking, index) => (
          <div key={index} className="invoice-page">
            {/* Header */}
            <div className="grid grid-cols-3 gap-2 mb-3 print-header">
              <div>
                <strong>
                  GAHH Automotive<br />
                  11128 Gault Street<br />
                  North Hollywood, CA 91605
                </strong><br />
                Tel: 714-708-5115<br />
                Fax: 714-545-8075
              </div>
              <div className="flex justify-center items-start">
                <img src="/images/gahh_logo.webp" alt="GAHH Automotive" style={{ maxHeight: '45px' }} />
              </div>
              <div className="text-right">
                <h2 className="print-title font-bold mb-1">
                  {isPacking ? 'Packing List' : 'Invoice'}
                </h2>
                Number: <strong className="print-number">
                  {isPacking ? order.GahhOrderNumber : order.GahhInvoiceNumber}
                </strong><br />
                Date: {formatDate(order.Today)}<br />
                <span className="text-gray-600">{order.OrderNumber}</span>
              </div>
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-2 gap-2 mb-3 print-header">
              {order.CustomerId !== '33ANY0792' && !isPacking && (
                <div>
                  <strong>Sold to:</strong>
                  <div>
                    {order.BillToName}<br />
                    {order.BillToAddress1}<br />
                    {order.BillToCity} {order.BillToState} {order.BillToZipCode}<br />
                    {order.BillToCountry}
                  </div>
                </div>
              )}
              <div>
                {order.DropShip && <div className="font-bold text-red-600">Drop Shipment</div>}
                <strong>Ship to:</strong>
                <div>
                  {order.ShipToName}<br />
                  {order.ShipToAddress1}<br />
                  {order.ShipToCity} {order.ShipToState} {order.ShipToZipCode}<br />
                  {order.ShipToCountry}
                </div>
              </div>
            </div>

            {/* Order Info */}
            <table className="w-full border border-gray-400 mb-2 print-table">
              <tbody>
                <tr>
                  <td colSpan={6} className="border-b border-gray-400">
                    <div className="grid grid-cols-3 gap-2 py-1 text-center">
                      <div>
                        <strong>Customer ID</strong><br />
                        {order.CustomerId}
                      </div>
                      <div>
                        <strong>Customer PO</strong><br />
                        {order.CustomerOrderNo}
                      </div>
                      <div>
                        <strong>Payment Terms</strong><br />
                        {order.Terms}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colSpan={6} className="border-b border-gray-400">
                    <div className="grid grid-cols-2 gap-2 py-1 text-center">
                      <div>
                        <strong>Shipping Method:</strong> {order.ShipVia}
                      </div>
                      <div>
                        <strong>Ship Date:</strong> {formatDate(order.Today)}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="bg-gray-200">
                  <td className="border-r border-gray-400 font-bold">Item No.</td>
                  <td className="border-r border-gray-400 font-bold">Part No.</td>
                  <td className="border-r border-gray-400 font-bold">Description</td>
                  <td className="border-r border-gray-400 font-bold text-center">Qty Ord</td>
                  <td className="border-r border-gray-400 font-bold text-center">Qty Ship</td>
                  {!isPacking && <td className="font-bold text-center">Price</td>}
                </tr>
                {order.Items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="border-r border-t border-gray-400 nowrap">
                      {order.OrderNumber}-{item.Ordinal}-{order.ItemCount}
                    </td>
                    <td className="border-r border-t border-gray-400 nowrap">{item.PartNumber}</td>
                    <td className="border-r border-t border-gray-400">{item.Description}</td>
                    <td className="border-r border-t border-gray-400 text-center">1.0</td>
                    <td className="border-r border-t border-gray-400 text-center">1.0</td>
                    {!isPacking && (
                      <td className="border-t border-gray-400 text-right">
                        ${item.Price.toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))}
                {order.DropShip && !isPacking && (
                  <tr>
                    <td className="border-r border-t border-gray-400"></td>
                    <td className="border-r border-t border-gray-400">DS-1</td>
                    <td className="border-r border-t border-gray-400">Drop Shipping Charge</td>
                    <td className="border-r border-t border-gray-400 text-center">1.0</td>
                    <td className="border-r border-t border-gray-400 text-center">0.0</td>
                    <td className="border-t border-gray-400 text-right">
                      ${order.DropShipPrice.toFixed(2)}
                    </td>
                  </tr>
                )}
                {order.ShipVia === 'C.O.D' && (
                  <tr>
                    <td colSpan={5} className="border-r border-t border-gray-400">{order.ShipVia}</td>
                    <td className="border-t border-gray-400 text-right">${calculateCODAmount().toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Footer */}
            <div className="print-footer">
              {!isPacking ? (
                <div className="flex justify-between">
                  <div>
                    <p className="mb-2">
                      <strong><em>OFFICE USE ONLY</em></strong><br />
                      - COMPLETE &nbsp;&nbsp; - DROP SHIP
                    </p>
                    <p>
                      Entered by: __________________ Date: ______________
                    </p>
                  </div>
                  <div className="text-right">
                    {order.DropShip && <p>{order.ItemCount} total shipped</p>}
                    <table className="ml-auto">
                      <tbody>
                        <tr>
                          <td className="text-right pr-2"><strong>Subtotal:</strong></td>
                          <td className="text-right">${calculateSubtotal().toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="text-right pr-2"><strong>Freight:</strong></td>
                          <td className="text-right">${order.Freight.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="text-right pr-2"><strong>Tax:</strong></td>
                          <td className="text-right">${order.Tax.toFixed(2)}</td>
                        </tr>
                        {calculateDiscount() > 0 && (
                          <tr>
                            <td className="text-right pr-2"><strong>Discount:</strong></td>
                            <td className="text-right">-${calculateDiscount().toFixed(2)}</td>
                          </tr>
                        )}
                        <tr className="print-total font-bold">
                          <td className="text-right pr-2 pt-1 border-t border-gray-400">TOTAL:</td>
                          <td className="text-right pt-1 border-t border-gray-400">${order.Total.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-right">{order.ItemCount} total shipped</p>
              )}
            </div>

            {!isPacking && (
              <div className="flex justify-center remit-banner">
                <p className="text-white bg-gray-700 px-4 py-2 text-center">
                  PLEASE REMIT PAYMENTS TO: GAHH Automotive 11128 Gault Street North Hollywood, CA 91605
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                CalTrend Shipping Station
              </h2>
              {listData && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {listData.totalInTrimmingPackaging} Items in Trimming/Packaging
                </p>
              )}
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search Sales Order..."
                className="mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-48"
              />
            </div>
            <div className="flex flex-col items-end gap-2">
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1 || isLoading}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || isLoading}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenReprintModal}
                  className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  Re-Print
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Label Printed — Ready to Receive */}
          {labelPrintedOrders.length > 0 && (
            <div className="border-b border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
              <div className="px-6 py-3 border-b border-green-200 dark:border-green-800 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                <h3 className="text-sm font-semibold text-green-800 dark:text-green-300">
                  Label Printed — Ready to Receive ({labelPrintedOrders.length})
                </h3>
              </div>
              <div className="divide-y divide-green-200 dark:divide-green-800">
                {labelPrintedOrders.map((ord: LabelPrintedOrder) => (
                  <div key={ord.SalesOrderNumber} className="px-6 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <div>
                        <div className="font-semibold text-green-900 dark:text-green-100 text-sm">
                          {ord.SalesOrderNumber}
                        </div>
                        <div className="text-xs text-green-700 dark:text-green-400">{ord.DaysOld}d old</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{ord.CustomerName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{ord.CustomerId}</div>
                      </div>
                      <div className="text-sm text-orange-600 dark:text-orange-400 font-semibold whitespace-nowrap">
                        {ord.ShipVia}
                        {ord.IsDropShip && <span className="ml-2 text-xs">DROP SHIP</span>}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {ord.PhysicalItemCount} items
                      </div>
                    </div>
                    <button
                      onClick={() => handleReceive(ord.SalesOrderNumber)}
                      disabled={getOrderItemsMutation.isPending}
                      className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Receive
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : !listData || listData.orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No orders in Trimming/Packaging status
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchQuery ? `No orders match "${searchQuery}"` : 'No orders in shipping'}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredOrders.map((ord: ShippingStationOrder, index: number) => (
                  <div
                    key={index}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    {/* Order Header */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <div className="font-bold text-gray-900 dark:text-gray-100">
                          {ord.CustomerId}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {ord.CustomerName}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Sales Order:</div>
                        <div className="font-semibold text-blue-600 dark:text-blue-400">
                          {ord.SalesOrderNumber}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {ord.ItemCount} Items
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Order Date:</div>
                        <div className="text-gray-900 dark:text-gray-100">{formatDate(ord.OrderDate)}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ord.DaysOld} days old</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Ship Via:</div>
                        <div className="font-semibold text-orange-600 dark:text-orange-400">
                          {ord.ShipVia}
                        </div>
                        {ord.IsDropShip && (
                          <div className="text-sm text-orange-600 dark:text-orange-400 font-semibold">
                            DROP SHIP
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-2 mb-4">
                      {ord.Items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="grid grid-cols-1 md:grid-cols-4 gap-2 py-2 border-t border-gray-300 dark:border-gray-600 text-sm"
                        >
                          <div className="font-mono text-gray-900 dark:text-gray-100">{item.ItemNumber}</div>
                          <div className="font-mono text-gray-900 dark:text-gray-100">{item.PartNumber}</div>
                          <div className="text-gray-600 dark:text-gray-400 truncate">
                            {item.Description}
                          </div>
                          <div>
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs ${getStatusBadgeClass(
                                item.Status
                              )}`}
                            >
                              {item.Status}
                            </span>
                            {item.StatusDate && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                on {formatStatusDate(item.StatusDate)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Order Footer */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300 dark:border-gray-600">
                      <div className="text-gray-900 dark:text-gray-100">
                        Order Status:{' '}
                        <span
                          className={`inline-block px-3 py-1 rounded text-sm font-semibold ${
                            ord.OrderStatus === 'Ready to Ship'
                              ? 'bg-green-600 text-white'
                              : 'bg-red-600 text-white'
                          }`}
                        >
                          {ord.OrderStatus}
                        </span>
                      </div>
                      <button
                        onClick={() => handleReceive(ord.SalesOrderNumber)}
                        disabled={getOrderItemsMutation.isPending}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {getOrderItemsMutation.isPending ? 'Loading...' : 'Receive'}
                      </button>
                    </div>
                  </div>
                ))}

              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages} &nbsp;({totalOrders} orders total)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1 || isLoading}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || isLoading}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receive Modal */}
      {showReceiveModal && receiveOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>

            <div className="relative inline-block w-full max-w-2xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Receive Items - Order {receiveOrder.orderNumber}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {receiveOrder.customerName} | {receiveOrder.shipVia}
                    {receiveOrder.isDropShip && <span className="ml-2 text-orange-500 font-semibold">DROP SHIP</span>}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scan Input */}
              <form onSubmit={handleScanSubmit} className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Scan or Enter Item Number
                </label>
                <div className="flex gap-2">
                  <input
                    ref={scanInputRef}
                    type="text"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    placeholder="e.g., 1167629-1-3"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={receiveItemMutation.isPending}
                  />
                  <button
                    type="submit"
                    disabled={receiveItemMutation.isPending || !scanInput.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {receiveItemMutation.isPending ? 'Receiving...' : 'Receive'}
                  </button>
                </div>
              </form>

              {/* Items List */}
              <div className="max-h-64 overflow-y-auto mb-4 border border-gray-200 dark:border-gray-700 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300">Item #</th>
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300">Part #</th>
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300">Status</th>
                      <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveItems.map((item, idx) => (
                      <tr key={idx} className={`border-t border-gray-200 dark:border-gray-700 ${item.isReceived ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                        <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{item.itemNumber}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.partNumber}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-1 rounded text-xs ${getStatusBadgeClass(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.isReceived ? (
                            <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Progress</span>
                  <span>{receiveItems.filter(i => i.isReceived).length} / {receiveItems.length} received</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${allReceived ? 'bg-green-600' : 'bg-indigo-600'}`}
                    style={{ width: `${(receiveItems.filter(i => i.isReceived).length / receiveItems.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {receiveOrder.isDropShip
                    ? 'Will print: 1 Invoice + 1 Packing List'
                    : 'Will print: 2 Invoices'}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                  <button
                    onClick={handlePrint}
                    disabled={!allReceived || getOrderMutation.isPending}
                    className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      allReceived
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {getOrderMutation.isPending ? 'Preparing...' : 'Print Invoice'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reprint Modal */}
      {showReprintModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseReprintModal}></div>

            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Re-Print Invoice & Packing Slip
                </h3>
                <button
                  onClick={handleCloseReprintModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Order Number Input */}
              <form onSubmit={handleReprintSubmit}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enter Order Number
                </label>
                <input
                  ref={reprintInputRef}
                  type="text"
                  value={reprintOrderNumber}
                  onChange={(e) => setReprintOrderNumber(e.target.value)}
                  placeholder="e.g., 1167629"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 mb-4"
                  disabled={reprintOrderMutation.isPending}
                />

                {/* Modal Footer */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleCloseReprintModal}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!reprintOrderNumber.trim() || reprintOrderMutation.isPending}
                    className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {reprintOrderMutation.isPending ? 'Loading...' : 'Print'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
